import {
    Box,
    Button,
    DialogActions,
    DialogContent,
    Divider,
    LinearProgress,
    Typography,
} from "@material-ui/core";
import { green } from "@material-ui/core/colors";
import { CheckCircle, Error } from "@material-ui/icons";
import { useImportDataControls } from "@hooks";
import { IMPORT_DATA_TYPE, ImportStatus } from "@hooks/useImportDataControls";
import React from "react";
import { useStoreon } from "storeon/react";
import { DropzoneField } from "../Controls/Inputs";
import { DialogContextProps } from "@interfaces/System/DialogContextProps";
import { TableEvents, TableState } from "@store/table-module";
import ImportCsvErrorDetails from "./ImportCsvErrorDetails";

type importDataModalProps = { entityTypeId: number };

const PHASE_LABEL: Record<ImportStatus["phase"], string> = {
    parsing: "Загрузка строк",
    applying: "Обновление данных в справочнике",
    syncing: "Перенос данных в каталог",
    done: "Готово",
    error: "Ошибка",
};

const ImportProgress = ({ status }: { status: ImportStatus }) => {
    if (status.phase === "done" || status.phase === "error") return null;

    const isParsing = status.phase === "parsing";
    const total = status.total ?? 0;
    const processed = status.processed ?? 0;
    const determinate = isParsing ? total > 0 : true;
    const value = isParsing
        ? total > 0
            ? Math.round((processed / total) * 100)
            : 0
        : Math.round(status.progress ?? 0);

    return (
        <Box py={1}>
            <Typography variant="body2" gutterBottom>
                {PHASE_LABEL[status.phase]}
                {isParsing && total > 0 ? `: ${processed} / ${total}` : "…"}
            </Typography>
            <LinearProgress
                variant={determinate ? "determinate" : "indeterminate"}
                value={value}
            />
            <Typography variant="caption" color="textSecondary">
                Можно закрыть окно — загрузка продолжится, результат появится в справочнике.
            </Typography>
        </Box>
    );
};

const ImportDataDialogContext = ({ onClose, modalProps }: DialogContextProps) => {
    const { entityTypeId = null } = { ...modalProps } as importDataModalProps;

    const { dispatch } = useStoreon<TableState, TableEvents>();

    const { handleSubmit, handleChangeFile, error, success, status } = useImportDataControls(
        modalProps.importType as IMPORT_DATA_TYPE,
        {
            entityTypeId: Number(entityTypeId),
            submitCallback: () => dispatch("table/update/data"),
        }
    );

    const inProgress =
        status !== null && status.phase !== "done" && status.phase !== "error";

    return (
        <>
            <DialogContent>
                <DropzoneField
                    onSubmit={handleSubmit}
                    maxFiles={1}
                    onDrop={handleChangeFile}
                    dropzoneOptions={{ accept: ".csv", disabled: inProgress }}
                    text={
                        <>
                            Разрешенные форматы: <b>.csv</b>
                        </>
                    }
                    emptyFilesLabel={status?.fileName}
                />

                {status && inProgress && (
                    <>
                        <Divider />
                        <ImportProgress status={status} />
                    </>
                )}

                {(error || success) && (
                    <>
                        <Divider />
                        <Box py={1}>
                            <Typography
                                gutterBottom
                                style={{ display: "flex", alignItems: "center" }}
                            >
                                {success && (
                                    <>
                                        <CheckCircle
                                            style={{ marginRight: 10, color: green[500] }}
                                        />
                                        Успешно!
                                    </>
                                )}
                                {error && (
                                    <>
                                        <Error style={{ marginRight: 10 }} color="error" />
                                        Ошибка!
                                    </>
                                )}
                            </Typography>
                            {success && <Typography variant="body2">{success}</Typography>}
                            {error && (
                                <ImportCsvErrorDetails
                                    error={error}
                                    entityTypeId={Number(entityTypeId)}
                                />
                            )}
                        </Box>
                    </>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Закрыть</Button>
            </DialogActions>
        </>
    );
};

export default ImportDataDialogContext;
